import { LoanFeePaymentDetails } from 'app/components/loan-fee-payment-details';
import { useTheme } from '@shopify/restyle';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Modal } from 'app/design/primitives/modal';
import { Text } from 'app/design/primitives/text';

import { addLoanToCalendar } from 'app/lib/calendar';
import { isDatePast, localeAwareFormat } from 'app/lib/date';
import type { LoanMessage as LoanMessageType, Message as MessageType } from 'app/types/chat';
import type { Loan } from 'app/types/loan';
import { useAcceptOneLoan, useOneLoan, useRejectOneLoan } from 'app/network/loan';
import { LoanStatus } from 'app/types/loan';
import { useOnePreference, useUpsertOnePreference } from 'app/network/preferences';
import { AddToCalendar } from 'app/types/preferences';
import { type Dispatch, type RefObject, type SetStateAction, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Toast from 'react-native-toast-message';
import type { ColorValue, ImageRequireSource } from 'react-native';
import { Keyboard, Pressable, View } from 'react-native';
import type { TFunction } from 'i18next';
import { ModalCalendarSetting } from 'app/screens/profile/my-preferences/calendar/modal';
import { Event, track } from 'app/lib/analytics';
import { useUser } from 'app/network/user';
import { useNavigation } from 'app/hooks/navigation';
import type { UserLevelSharerSignalData } from 'app/network/signals';
import { getSignalData, hasSignal, Signal } from 'app/network/signals';
import { UserLevel } from 'app/schemas/user';
import { images } from 'app/design/images';
import { Image } from 'expo-image';
import ConfettiCannon from 'react-native-confetti-cannon';
import { useIsAuthor } from 'app/network/chat';
import { StorageKey } from 'app/lib/storageKeys';
import { useStoredValue } from 'app/lib/storage';
import { useOfferAvailabilityPopup } from 'app/screens/management/refrain-offer-availability-popup';
import type { RefetchOneChannel } from 'app/network/chat/channel';
import { isSameDay } from 'date-fns';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import type { LoanFeeModalsState } from 'app/screens/management/loan-fee';
import { isMobileNative } from 'app/utils/web-helpers';
import type { FlashOrFlatList } from 'app/design/primitives/page/list';
import type { Theme } from 'app/shopify';
import {
  IconAlertTriangle,
  IconCalendar,
  IconCalendarCheck,
  IconCalendarClock,
  IconEdit,
  IconInfoCircle,
  IconTrash,
} from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isDonationListing } from 'app/lib/listing';

interface LoanMessageProps {
  message: LoanMessageType;
  isLender: boolean;
  isSuperseded: boolean;
  textColor: ColorValue;
  listRef?: RefObject<FlashOrFlatList<MessageType, null>>;
  loanFeeModalsState: LoanFeeModalsState;
  refetchChannel: RefetchOneChannel;
}

const buildContent = (isLender: boolean, loan: Loan, hasSentTheProposition: boolean, t: TFunction) => {
  const isParticipations = loan.listing.maxParticipants && loan.listing.maxParticipants > 0;
  const prefix = isParticipations
    ? 'participationsMessage'
    : loan.listing.subtype === ListingSubtype.service
      ? 'serviceMessage'
      : isDonationListing(loan.listing)
        ? 'donationMessage'
        : loan.listing.transactionMode === TransactionMode.sell
          ? 'sellMessage'
          : 'loanMessage';

  if (loan.status === LoanStatus.Pending && !hasSentTheProposition) {
    return loan.isUpdateFor ? t(`${prefix}.contentPendingIsUpdate`) : t(`${prefix}.contentPending`);
  }

  if (loan.status === LoanStatus.Accepted) {
    return t(`${prefix}.contentAccepted`);
  }

  if (loan.status === LoanStatus.Rejected) {
    return t(`${prefix}.contentRejected`);
  }

  if (loan.status === LoanStatus.Completed) {
    return t(`${prefix}.contentCompleted`);
  }

  return isLender ? t(`${prefix}.contentAsLender`) : t(`${prefix}.contentAsBorrower`);
};

const getLoanMessageDisplayState = (loan: Loan) => {
  const isService = loan.listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(loan.listing);
  const isParticipations = Boolean(loan.listing.maxParticipants && loan.listing.maxParticipants > 0);
  const tPrefix = isParticipations
    ? 'participationsMessage'
    : isService
      ? 'serviceMessage'
      : isDonation
        ? 'donationMessage'
        : loan.listing.transactionMode === TransactionMode.sell
          ? 'sellMessage'
          : 'loanMessage';
  const LoanStatusIcon =
    loan.status === LoanStatus.Pending
      ? IconCalendarClock
      : loan.status === LoanStatus.Accepted
        ? IconCalendarCheck
        : IconCalendar;
  const loanStartDate = new Date(loan.start);
  const loanEndDate = new Date(loan.end);
  return {
    LoanStatusIcon,
    loanEndDate,
    loanStartDate,
    showEndDatePassed:
      isDatePast(loanEndDate) && !loan.isUpdateFor && loan.status === LoanStatus.Pending && !(isService || isDonation),
    showStartDatePassed: isDatePast(loanStartDate) && !loan.isUpdateFor && loan.status === LoanStatus.Pending,
    tPrefix,
  };
};

type LoanMessageDisplayState = ReturnType<typeof getLoanMessageDisplayState>;

const LoanDatePeriod = ({
  displayState,
  flexibleDates,
  t,
  textColor,
}: {
  displayState: LoanMessageDisplayState;
  flexibleDates: boolean | undefined;
  t: TFunction;
  textColor: ColorValue;
}) => {
  const { loanEndDate, loanStartDate } = displayState;
  const sameDay = isSameDay(loanStartDate, loanEndDate);
  const period = sameDay
    ? localeAwareFormat(loanStartDate, 'd MMM yyyy')
    : `${localeAwareFormat(loanStartDate, 'd MMM yyyy')} ${t('loanMessage.to')} ${localeAwareFormat(
        loanEndDate,
        'd MMM yyyy',
      )}`;
  return (
    <Text marginTop="spacing.general.x4" style={{ color: textColor }} variant="bodyBase">
      {sameDay ? t('loanMessage.date') : t('loanMessage.period')} :{' '}
      <Text fontWeight="700" style={{ color: textColor }}>
        {period}
      </Text>
      {flexibleDates ? ` ${t('loanMessage.flexibleDates')}` : ''}
    </Text>
  );
};

const LoanDateWarnings = ({
  displayState,
  t,
  textColor,
}: {
  displayState: LoanMessageDisplayState;
  t: TFunction;
  textColor: ColorValue;
}) => (
  <>
    {displayState.showStartDatePassed ? (
      <Row alignItems="center" gap="spacing.general.x1" marginTop="spacing.general.x2">
        <IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} color={textColor} />
        <Text flex={1} style={{ color: textColor }} variant="bodyBase">
          {t(`${displayState.tPrefix}.startDatePassed`)}
        </Text>
      </Row>
    ) : null}
    {displayState.showEndDatePassed ? (
      <Row alignItems="center" gap="spacing.general.x1" marginTop="spacing.general.x2">
        <IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} color={textColor} />
        <Text flex={1} style={{ color: textColor }} variant="bodyBase">
          {t(`${displayState.tPrefix}.endDatePassed`)}
        </Text>
      </Row>
    ) : null}
  </>
);

const LoanFeeDetails = ({
  isLender,
  loan,
  loanFeeModalsState,
  t,
  textColor,
  theme,
}: {
  isLender: boolean;
  loan: Loan;
  loanFeeModalsState: LoanFeeModalsState;
  t: TFunction;
  textColor: ColorValue;
  theme: Theme;
}) => {
  if (!loan.fee)
    return loan.feeDocument ? <LoanFeePaymentDetails document={loan.feeDocument} showPaymentMethod={false} /> : null;
  return (
    <Pressable
      onPress={isLender ? undefined : () => loanFeeModalsState.setIsLoanFeeOtherModalOpen(true)}
      style={{ marginTop: theme.spacing['spacing.general.x4'] }}
    >
      <Column gap="spacing.general.x0,5">
        <Row alignItems="center" gap="spacing.general.x0,5">
          <Text style={{ fontWeight: '700', color: textColor }} variant="bodyBase">
            {t('loanFee.title', {
              ns: 'one-listing',
              amount: t('x$', { ns: 'common', amount: loan.fee.toFixed(loan.fee % 1 === 0 ? 0 : 2) }),
            })}
          </Text>
          {!isLender ? (
            <View style={{ marginVertical: -8 }}>
              <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} color="transparent" />
            </View>
          ) : null}
        </Row>
        {loan.feeDescription ? <Text variant="bodyBase">{loan.feeDescription}</Text> : null}
        <LoanFeePaymentDetails
          document={loan.feeDocument}
          paymentMethod={loan.feePaymentMethod}
          showPaymentMethod={!isLender}
        />
      </Column>
    </Pressable>
  );
};

const LoanDecision = ({
  acceptPending,
  isSuperseded,
  isAuthor,
  isLender,
  loan,
  onAccept,
  onReject,
  rejectPending,
  t,
  textColor,
  tPrefix,
  theme,
}: {
  acceptPending: boolean;
  isSuperseded: boolean;
  isAuthor: boolean;
  isLender: boolean;
  loan: Loan;
  onAccept: () => void;
  onReject: () => void;
  rejectPending: boolean;
  t: TFunction;
  textColor: ColorValue;
  tPrefix: string;
  theme: Theme;
}) => {
  if (isAuthor || loan.status !== LoanStatus.Pending) {
    return (
      <Text marginTop="spacing.general.x4" style={{ color: textColor }} variant="bodyBase">
        {isLender ? t(`${tPrefix}.loanStatusAsLender`) : t(`${tPrefix}.loanStatusAsBorrower`)} :{' '}
        <Text fontWeight="700">{t(`${tPrefix}.status.${loan.status}`)}</Text>
      </Text>
    );
  }
  return (
    <Row gap="spacing.general.x4" marginTop="spacing.general.x4">
      <Button
        disabled={isSuperseded || acceptPending}
        flex={1}
        loading={rejectPending}
        onPress={onReject}
        style={{ backgroundColor: theme.colors['surface.default'] }}
        variant="secondary"
      >
        {t('loanMessage.reject')}
      </Button>
      <Button
        disabled={isSuperseded || rejectPending}
        flex={1}
        loading={acceptPending}
        onPress={onAccept}
        variant="primary"
      >
        {t('loanMessage.accept')}
      </Button>
    </Row>
  );
};

interface LevelModalState {
  isVisible: boolean;
  i18nKey: string;
  numSharesLeft: number;
  levelImage: ImageRequireSource | undefined;
  launchConfetti: boolean;
}

const LoanMessageModals = ({
  isCalendarModalOpened,
  levelModalState,
  loan,
  modalCalendarSettingState,
  onAddToCalendar,
  onCloseCalendar,
  onCloseLevel,
  onSetAlways,
  t,
  theme,
}: {
  isCalendarModalOpened: boolean;
  levelModalState: LevelModalState;
  loan: Loan;
  modalCalendarSettingState: [boolean, Dispatch<SetStateAction<boolean>>];
  onAddToCalendar: (loan: Loan) => void;
  onCloseCalendar: () => void;
  onCloseLevel: (navigate?: boolean) => void;
  onSetAlways: () => void;
  t: TFunction;
  theme: Theme;
}) => (
  <>
    <Modal onClose={onCloseLevel} open={levelModalState.isVisible} transparent>
      <Modal.Card>
        <Modal.Title>{t(`levelModal.${levelModalState.i18nKey}.title`)}</Modal.Title>
        <Row alignItems="center" gap="spacing.general.x4">
          <Image source={levelModalState.levelImage} style={{ flex: 1, maxWidth: 60, height: 60 }} />
          <Text style={{ flex: 2 }} textAlign="center">
            <Trans
              components={{ strong: <Text fontWeight="700" /> }}
              i18nKey={`levelModal.${levelModalState.i18nKey}.message`}
              ns="one-channel"
              values={{ numLeft: levelModalState.numSharesLeft }}
            />
          </Text>
        </Row>
        <View>
          <Button marginBottom="spacing.general.x4" onPress={() => onCloseLevel(true)} variant="primary">
            {t('levelModal.primaryButtonText')}
          </Button>
          <Button onPress={() => onCloseLevel()} variant="secondary">
            {t('levelModal.secondaryButtonText')}
          </Button>
        </View>
        {levelModalState.launchConfetti ? (
          <ConfettiCannon
            colors={[theme.colors['surface.tertiary']]}
            count={70}
            fallSpeed={5000}
            origin={{ x: 0, y: 0 }}
          />
        ) : null}
      </Modal.Card>
    </Modal>
    <Modal onClose={onCloseCalendar} open={isCalendarModalOpened} transparent>
      <Modal.Card>
        <Modal.Title>{t('addToCalendar.modal.title')}</Modal.Title>
        <Column>
          <Text textAlign="center">{t('addToCalendar.modal.description')}</Text>
        </Column>
        <View>
          <Button
            marginBottom="spacing.general.x4"
            onPress={() => {
              onCloseCalendar();
              onSetAlways();
              onAddToCalendar(loan);
            }}
            variant="primary"
          >
            {t('addToCalendar.modal.always')}
          </Button>
          <Button
            marginBottom="spacing.general.x4"
            onPress={() => {
              onCloseCalendar();
              onAddToCalendar(loan);
            }}
            variant="primary"
          >
            {t('addToCalendar.modal.once')}
          </Button>
          <Button onPress={onCloseCalendar} variant="secondary">
            {t('addToCalendar.modal.discard')}
          </Button>
        </View>
      </Modal.Card>
    </Modal>
    <ModalCalendarSetting modalState={modalCalendarSettingState} />
  </>
);

export function LoanMessage({
  message,
  isLender,
  isSuperseded,
  textColor,
  listRef,
  loanFeeModalsState,
  refetchChannel,
}: LoanMessageProps) {
  const theme = useTheme<Theme>();
  const { data: user } = useUser();
  const navigation = useNavigation();
  const isAuthor = useIsAuthor();
  const { t } = useTranslation('one-channel');

  // Reload loan info when it gets updated; otherwise, just use the value from message.loan
  const { data: updatedLoan, refetch: refetchLoan } = useOneLoan(message.loan.id, {
    enabled: false,
  });
  const loan = updatedLoan || message.loan;

  const [levelModalState, setLevelModalState] = useState<LevelModalState>({
    isVisible: false,
    i18nKey: '',
    numSharesLeft: 0,
    levelImage: undefined as ImageRequireSource | undefined,
    launchConfetti: false,
  });

  const closeLevelModal = (navigate?: boolean) => {
    setLevelModalState({
      ...levelModalState,
      isVisible: false,
    });
    if (navigate) {
      // navigation.navigate('profile', { screen: 'my-impact', initial: false });
    }
  };

  const showPopupMaybe = useOfferAvailabilityPopup(StorageKey.RefrainBlockedDatesPopupWasShownLenderAfter, navigation, {
    listingId: loan.listingId,
    isSaleOrDonation: loan.listing.transactionMode === TransactionMode.sell || isDonationListing(loan.listing),
  });

  const acceptOneLoan = useAcceptOneLoan({
    onSuccess(acceptedLoan) {
      void refetchChannel().then(() => {
        void listRef?.current?.scrollToIndex({ index: 0, animated: true }); // scroll to bottom (list is inverted, so index 0 = bottom)
      });
      const isNowSuper = hasSignal(acceptedLoan, Signal.UserIsNowSuper);
      if (isNowSuper) {
        // @TODO Show modal "Tu es maintenant Super Partageur"
      }
      // This is deprecated:
      const signalData = getSignalData(acceptedLoan, Signal.UserLevelSharer) as UserLevelSharerSignalData | undefined;
      if (signalData) {
        if (signalData.level === UserLevel.SUPER_SHARER) {
          setLevelModalState({
            ...levelModalState,
            isVisible: true,
            i18nKey: 'superSharer',
            levelImage: images.badge.checked.superSharer,
            launchConfetti: true,
          });
        } else {
          setLevelModalState({
            ...levelModalState,
            isVisible: true,
            i18nKey: signalData.numLoans === 1 ? 'sharer1' : signalData.numLoans === 4 ? 'sharer4' : 'sharer23',
            levelImage: images.badge.sharer,
            numSharesLeft: 5 - signalData.numLoans,
          });
        }
      }
      // This reminder is useful after accepting a new loan, not after merely
      // replacing the dates of a loan that was already accepted.
      if (isLender && !acceptedLoan.isUpdateFor) {
        showPopupMaybe();
      }
    },
  });
  const rejectOneLoan = useRejectOneLoan({
    onSuccess() {
      void refetchLoan();
    },
  });

  const [isCalendarModalOpened, setIsCalendarModalOpened] = useState(false);
  const { refetch: refetchAddToCalendarPreference } = useOnePreference('addToCalendar');
  const upsertAddToCalendarPreference = useUpsertOnePreference('addToCalendar');

  const [calendarPrompts, setCalendarPrompts] = useStoredValue<string[]>(StorageKey.CalendarPrompts, []);
  const [loginAsUserId] = useStoredValue<string>(StorageKey.LoginAsUserId);

  const modalCalendarSettingState = useState(false);
  const setCalendarSettingModalOpen = modalCalendarSettingState[1];

  const addToCalendar = async (l?: Loan) => {
    if (!user?.id) return;
    const res = await addLoanToCalendar(t, user.id, l);
    if (res === 'permission-denied') {
      setCalendarSettingModalOpen(true);
    } else if (res === 'error') {
      Toast.show({
        type: 'error',
        text1: t('genericError', { ns: 'common' }),
        position: 'bottom',
        bottomOffset: 100,
        visibilityTime: 3000,
      });
    }
  };

  useEffect(() => {
    if (loan.status !== LoanStatus.Accepted) return;
    if (!isMobileNative) return;
    if (new Date(loan.end).getTime() < Date.now()) return;
    if (loginAsUserId) return; // do not add to calendar when impersonating another user

    // Execute this just once per loan.
    if (calendarPrompts === undefined || calendarPrompts.includes(loan.id)) return;

    // Mark synchronously before the async preference read so rerenders cannot enqueue this loan twice.
    setCalendarPrompts([...calendarPrompts, loan.id]);
    void refetchAddToCalendarPreference().then(({ data: addToCalendarData }) => {
      if (addToCalendarData?.value === AddToCalendar.once) {
        track(Event.CalendarAddLoanModalOpened);
        Keyboard.dismiss();
        setIsCalendarModalOpened(true);
        return;
      }

      if (addToCalendarData?.value === AddToCalendar.always && user?.id) {
        void addLoanToCalendar(t, user.id, loan).then((result) => {
          if (result === 'permission-denied') {
            setCalendarSettingModalOpen(true);
          } else if (result === 'error') {
            Toast.show({
              type: 'error',
              text1: t('genericError', { ns: 'common' }),
              position: 'bottom',
              bottomOffset: 100,
              visibilityTime: 3000,
            });
          }
        });
      }
    });
  }, [
    calendarPrompts,
    loan,
    loginAsUserId,
    refetchAddToCalendarPreference,
    setCalendarPrompts,
    setCalendarSettingModalOpen,
    t,
    user?.id,
  ]);

  const displayState = getLoanMessageDisplayState(loan);
  const hasSentTheProposition = isAuthor(message);
  const { LoanStatusIcon, tPrefix } = displayState;

  return (
    <Column padding="spacing.general.x3" gap="spacing.general.x3">
      <Row justifyContent="center">
        <LoanStatusIcon strokeWidth={ICON_STROKE_WIDTH * 0.9} size={60} color={textColor} />
      </Row>

      <Column>
        {/* Row is needed here because without it, the text can be cut off... */}
        <Text fontWeight="700" marginTop="spacing.general.x2" style={{ color: textColor }} variant="bodyBase">
          {buildContent(isLender, loan, hasSentTheProposition, t)}
        </Text>
        <LoanDatePeriod {...{ displayState, t, textColor }} flexibleDates={loan.flexibleDates} />
      </Column>

      <LoanDateWarnings {...{ displayState, t, textColor }} />
      <LoanFeeDetails {...{ isLender, loan, loanFeeModalsState, t, textColor, theme }} />
      <LoanDecision
        acceptPending={acceptOneLoan.isPending}
        isSuperseded={isSuperseded}
        isAuthor={hasSentTheProposition}
        {...{ isLender, loan, t, textColor, tPrefix, theme }}
        onAccept={() => acceptOneLoan.mutate(loan.id)}
        onReject={() => rejectOneLoan.mutate(loan.id)}
        rejectPending={rejectOneLoan.isPending}
      />
      {isAuthor(message) &&
      loan.status === LoanStatus.Pending &&
      !(loan.listing.maxParticipants && loan.listing.maxParticipants > 0) ? (
        <Row gap="spacing.general.x2">
          <Button
            disabled={isSuperseded}
            flex={1}
            loading={rejectOneLoan.isPending}
            onPress={() => rejectOneLoan.mutate(loan.id)}
            prefix={
              <IconTrash
                strokeWidth={ICON_STROKE_WIDTH}
                size={20}
                color={theme.colors[isSuperseded || rejectOneLoan.isPending ? 'text.tertiary' : 'text.error']}
              />
            }
            textAlign="center"
            variant="delete"
          >
            {t('loanMessage.cancel')}
          </Button>
          <Button
            disabled={isSuperseded || rejectOneLoan.isPending}
            flex={1}
            onPress={() =>
              navigation.navigate(
                'loan-dates-picker',
                {
                  loanId: loan.id,
                  url: loan.channelURL,
                  promptIntroduceYourself: false,
                  goBackTo: 'one-channel',
                },
                { pop: true },
              )
            }
            prefix={
              <IconEdit
                strokeWidth={ICON_STROKE_WIDTH}
                size={20}
                color={theme.colors[isSuperseded || rejectOneLoan.isPending ? 'text.tertiary' : 'black']}
              />
            }
            style={{ backgroundColor: theme.colors['surface.default'] }}
            textAlign="center"
            variant="secondary"
          >
            {t('loanMessage.edit')}
          </Button>
        </Row>
      ) : null}

      <LoanMessageModals
        {...{ isCalendarModalOpened, levelModalState, loan, modalCalendarSettingState, t, theme }}
        onAddToCalendar={(selectedLoan) => void addToCalendar(selectedLoan)}
        onCloseCalendar={() => setIsCalendarModalOpened(false)}
        onCloseLevel={closeLevelModal}
        onSetAlways={() => upsertAddToCalendarPreference.mutate(AddToCalendar.always)}
      />
    </Column>
  );
}
